import { useState, useRef, useMemo, useEffect, MouseEventHandler } from 'react';
import { getGlobalStore } from '@/store/global';
import classNames from 'classnames';
import './index.scss';
import { recoverBodyScroll, stopBodyScroll } from '@/utils';

const PicturePreview = () => {
    const scalesList = useRef([0.2, 0.4, 0.6, 0.8, 1, 1.2, 1.4, 1.6, 1.8, 2]);
    const [scaleIndex, setScaleIndex] = useState(4);
    const [pictureUrl, setPictureUrl] = useState('');
    const [translate, setTranslate] = useState({ x: 0, y: 0 });
    const [rotate, setRotate] = useState(0);
    const [visible, setVisible] = useState(false);
    const startPostion = useRef<{ x: number; y: number }>();
    const oldTranslate = useRef({ x: 0, y: 0 });

    const scaleNum = useMemo(() => scalesList.current[scaleIndex], [scaleIndex]);

    const handleEnlarge = () => {
        if (scaleIndex === scalesList.current.length - 1) return;
        setScaleIndex(scaleIndex + 1);
    };

    const handleNarrow = () => {
        if (scaleIndex === 0) return;
        setScaleIndex(scaleIndex - 1);
    };

    const handleTurnLeft = () => {
        setRotate(oldRotate => oldRotate + 90);
    };

    const handleTurnRight = () => {
        setRotate(oldRotate => oldRotate - 90);
    };

    const handleDownload = () => {
        const img = new window.Image();
        img.crossOrigin = 'Anonymous';
        img.src = pictureUrl;
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas?.getContext('2d');
            canvas.width = img.width;
            canvas.height = img.height;
            ctx?.drawImage(img, 0, 0);
            const link = document.createElement('a');
            link.download = 'image.png';
            link.href = canvas.toDataURL();
            link.click();
        };
    };

    const handleMouseUp = () => {
        startPostion.current = undefined;
        oldTranslate.current = translate;
    };

    const handleMouseMove: MouseEventHandler<HTMLImageElement> = ev => {
        if (!startPostion.current) return;
        const movex = ev.pageX - startPostion.current.x + oldTranslate.current.x;
        const moveY = ev.pageY - startPostion.current.y + oldTranslate.current.y;
        // console.log('handleMouseMove', movex, moveY);
        setTranslate({ x: movex, y: moveY });
    };

    const handleMouseDown: MouseEventHandler<HTMLImageElement> = ev => {
        // console.log('handleMouseDown', ev.pageX, ev.pageY);
        startPostion.current = { x: ev.pageX, y: ev.pageY };
    };

    useEffect(() => {
        getGlobalStore()?.subscribe('PicturePreview', ({ url }: { url: string }) => {
            setScaleIndex(4);
            setPictureUrl(url);
            setVisible(true);
            setRotate(0);
            setTranslate({ x: 0, y: 0 });
            oldTranslate.current = { x: 0, y: 0 };
        });
    }, []);

    useEffect(() => {
        visible ? stopBodyScroll() : recoverBodyScroll();
    }, [visible]);

    return (
        <div className="picture-preview-container" style={{ display: visible ? 'block' : 'none' }} onMouseUp={handleMouseUp}>
            <div className="picture-preview-container__close" onClick={() => setVisible(false)}></div>
            <img
                className="picture-preview-container__picture"
                style={{ transform: `translate(${translate.x}px, ${translate.y}px) scale(${scaleNum}) rotate(${rotate}deg)` }}
                src={pictureUrl}
                onMouseMove={handleMouseMove}
                onMouseDown={handleMouseDown}
                draggable={false}
            />
            <div className="picture-preview-container__operations">
                {/* <div className="picture-preview-container__download" onClick={handleDownload}></div> */}
                <div
                    className={classNames(
                        'picture-preview-container__enlarge',
                        scaleNum === scalesList.current[scalesList.current.length - 1] && 'picture-preview-container__enlarge--disabled'
                    )}
                    onClick={handleEnlarge}
                ></div>
                <div
                    className={classNames(
                        'picture-preview-container__narrow',
                        scaleNum === scalesList.current[0] && 'picture-preview-container__narrow--disabled'
                    )}
                    onClick={handleNarrow}
                ></div>
                <div className="picture-preview-container__turn-left" onClick={handleTurnLeft}></div>
                <div className="picture-preview-container__turn-right" onClick={handleTurnRight}></div>
            </div>
        </div>
    );
};

export default PicturePreview;
